/**
 * GenerationToastBridge
 *
 * Watches the live notifications list for `kind === 'info'` generation
 * events (Stage 3 video publish, single block-gen complete) and surfaces
 * them as passive Sonner toasts — never blocking.
 *
 * Action-required + failure events are handled by <ActionDialogQueue />.
 *
 * Renders nothing; lives at the app root so it sees every WS event.
 *
 * ─── 2026-08-04 — Historical-notification re-toast bug fix ───
 * User report: "Block ready" toasts fire on every login/refresh even for
 * jobs completed 30 days ago, and clicking Open / dismissing doesn't stop
 * them coming back. Root cause chain:
 *   1. `NotificationContext.fetchNotifications({limit:50})` returns the
 *      full unread-plus-recent history at boot.
 *   2. `seenRef` was `useRef(new Set())` — reset to empty on every mount
 *      (login, logout, hard-refresh, or org switch), so historical items
 *      were treated identically to live-arriving WS events.
 *   3. The filter did not check `n.isRead`, so even after the bell
 *      dropdown had been used to dismiss / mark-all-read, historical
 *      items still qualified for a toast.
 *   4. Clicking "Open" on the toast just navigated — it never marked the
 *      underlying notification as read, so it stayed in the unread bucket
 *      forever.
 *
 * Fix (all in this file — no backend changes):
 *   a. On first render, pre-populate `seenRef` with every id from the
 *      initial `notifications` snapshot BEFORE running the toast loop.
 *      Only notifications that arrive AFTER first render (i.e. live via
 *      WebSocket) get toasted.
 *   b. Skip anything with `n.isRead === true` — read history should never
 *      toast, even if the seen-set has been cleared somehow.
 *   c. Persist `seenRef` to localStorage keyed by user id. A hard refresh
 *      or same-tab re-login rehydrates the set, so previously-toasted
 *      notifications never re-fire.
 *   d. When we DO fire a toast, best-effort mark the notification as
 *      read on the backend. The user's Open-click or timeout-dismiss
 *      then permanently removes it from the unread bucket, so even a
 *      cross-device fresh session won't re-toast.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useNotificationContext } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import useIsInFocusedFlow from '../hooks/useIsInFocusedFlow';
import api from '../api/client';

const INFO_TYPES = new Set([
  'video_stage_complete',     // Stage 3 hits 'completed' status → kind:info
  'block_gen_complete',
  'batch_complete',           // when all succeeded with no in-progress
]);

// localStorage key prefix — one seen-set per user so switching accounts
// on the same browser doesn't leak dedup state. Capped size prevents
// unbounded growth (5 MB localStorage limit).
const SEEN_KEY_PREFIX = 'elv.genToast.seen.';
const SEEN_CAP = 500;

function _readSeen(userId) {
  if (!userId) return new Set();
  try {
    const raw = localStorage.getItem(`${SEEN_KEY_PREFIX}${userId}`);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch (_e) {
    return new Set();
  }
}

function _writeSeen(userId, set) {
  if (!userId) return;
  try {
    // Cap the set at SEEN_CAP entries — drop oldest (Set preserves insertion order).
    let arr = Array.from(set);
    if (arr.length > SEEN_CAP) arr = arr.slice(arr.length - SEEN_CAP);
    localStorage.setItem(`${SEEN_KEY_PREFIX}${userId}`, JSON.stringify(arr));
  } catch (_e) { /* ignore quota / private-mode errors */ }
}

export default function GenerationToastBridge() {
  const { notifications, markAsRead } = useNotificationContext();
  const { user, switchOrganization } = useAuth();
  // Hydrated from localStorage on first mount so we don't re-toast on refresh.
  const seenRef = useRef(null);
  // Guards the "prime with initial snapshot" pass so we only do it once
  // per (user, mount) — subsequent notification-list updates should be
  // treated as potentially-new events.
  const primedRef = useRef(false);
  const navigate = useNavigate();
  // 2026-02-24 — Suppress sonner toasts while the user is inside a
  // focused-work route (lab runner / immersive content / preview).
  // Toasts would otherwise fade in over the lab canvas and steal the
  // learner's attention. On next non-focused route, unseen events
  // will still trigger since we always mark `seenRef` regardless.
  const isFocusedFlow = useIsInFocusedFlow();

  // Rehydrate the seen-set every time the authenticated user changes
  // (login, logout, account switch). Persisted across page refreshes;
  // scoped per-user so accounts on the same browser stay independent.
  // Also dismiss any in-flight sonner toasts on logout so the login
  // screen doesn't briefly show the previous session's "Block ready"
  // toast lingering out its 6s duration.
  useEffect(() => {
    seenRef.current = _readSeen(user?.id);
    primedRef.current = false;
    if (!user?.id) {
      try { toast.dismiss(); } catch (_e) { /* no-op */ }
    }
  }, [user?.id]);

  const ensureOrgContextThenNavigate = async (link, targetOrgId) => {
    if (targetOrgId && targetOrgId !== user?.orgId) {
      try {
        const res = await api.get(`/organizations/${targetOrgId}`);
        if (res?.data?.id) switchOrganization(res.data);
      } catch (e) {
        console.warn('[GenerationToast] org switch failed:', e);
      }
    }
    navigate(link);
  };

  useEffect(() => {
    if (!user?.id) return;
    if (!notifications || notifications.length === 0) return;
    // Ensure the seen-set is hydrated (the effect above may not have
    // run yet on the very first render).
    if (seenRef.current === null) seenRef.current = _readSeen(user.id);

    // FIRST PASS after login / mount: treat every notification in the
    // initial snapshot as "already seen". This is the core of the
    // historical-re-toast fix — only notifications that arrive AFTER
    // this priming step (via WebSocket `new_notification` events that
    // prepend to the list) will pass the seen-check below.
    if (!primedRef.current) {
      primedRef.current = true;
      let changed = false;
      for (const n of notifications) {
        if (!seenRef.current.has(n.id)) {
          seenRef.current.add(n.id);
          changed = true;
        }
      }
      if (changed) _writeSeen(user.id, seenRef.current);
      return; // Do NOT toast anything on the priming pass.
    }

    // STEADY-STATE: any notification that isn't in the seen-set is a
    // fresh WS arrival. Toast + persist + best-effort mark-as-read.
    let changed = false;
    for (const n of notifications) {
      if (seenRef.current.has(n.id)) continue;
      seenRef.current.add(n.id);
      changed = true;

      if (!INFO_TYPES.has(n.type)) continue;
      const kind = n?.metadata?.kind;
      if (kind !== 'info') continue;
      // Skip anything already marked read — belt-and-braces guard so
      // a notification that arrives already-read (unusual, but seen in
      // cross-device scenarios where another tab acked it first) never
      // re-toasts.
      if (n.isRead) continue;
      // Skip surfacing the toast while the user is in a focused
      // route. `seenRef` was already marked above, so we won't
      // re-toast when they leave — matches the "mark-as-seen while
      // muted" semantics of most chat apps.
      if (isFocusedFlow) continue;
      const targetOrgId = n?.metadata?.targetOrgId;
      toast.success(n.title, {
        description: n.message,
        duration: 6000,
        action: n.link ? {
          label: 'Open',
          onClick: () => {
            // Mark read on click so a cross-device fresh session (or
            // another tab of the same session) never re-toasts.
            markAsRead(n.id).catch(() => {});
            ensureOrgContextThenNavigate(n.link, targetOrgId);
          },
        } : undefined,
      });
      // Best-effort mark-as-read on toast fire itself so time-out
      // dismissals (user never clicks anything) also clear the unread
      // state. Sonner's programmatic dismiss doesn't fire an event we
      // can hook into, so this is the pragmatic path. Silent-fail if
      // the backend is unreachable — we've already toasted locally.
      markAsRead(n.id).catch(() => {});
    }
    if (changed) _writeSeen(user.id, seenRef.current);
  }, [notifications, navigate, user?.id, user?.orgId, isFocusedFlow, markAsRead]);

  return null;
}
